import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

class TimezoneService {
  private userTimezone: string | null = null;
  private organizationTimezone: string | null = null;

  /**
   * Get user's current timezone (user preference → org default → browser detected)
   */
  getUserTimezone(): string {
    // If user has explicitly set a timezone (not null), use it
    if (this.userTimezone) {
      return this.userTimezone;
    }
    
    // If user timezone is null (default), follow organization timezone
    if (this.organizationTimezone) {
      return this.organizationTimezone;
    }
    
    // Final fallback to browser detection
    return this.getBrowserTimezone();
  }

  /**
   * Set user's timezone preference
   */
  setUserTimezone(timezone: string | null): void {
    this.userTimezone = timezone;
    if (timezone) {
      localStorage.setItem('userTimezone', timezone);
    } else {
      localStorage.removeItem('userTimezone');
    }
  }

  /**
   * Get organization's default timezone
   */
  getOrganizationTimezone(): string | null {
    return this.organizationTimezone;
  }

  /**
   * Set organization's default timezone
   */
  setOrganizationTimezone(timezone: string): void {
    this.organizationTimezone = timezone;
  }

  /**
   * Get browser's detected timezone
   */
  getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }

  /**
   * Convert UTC timestamp to user's timezone
   */
  convertToUserTimezone(utcTimestamp: Date | string): dayjs.Dayjs {
    const userTz = this.getUserTimezone();
    return dayjs.utc(utcTimestamp).tz(userTz);
  }

  /**
   * Convert user's local time to UTC
   */
  convertToUTC(localTime: Date | string, timezone?: string): dayjs.Dayjs {
    const tz = timezone || this.getUserTimezone();
    return dayjs.tz(localTime, tz).utc();
  }

  /**
   * Format time with timezone indicator
   */
  formatWithTimezone(date: Date | string, format = 'MMM D, YYYY h:mm A z'): string {
    return this.convertToUserTimezone(date).format(format);
  }

  /**
   * Get timezone abbreviation (e.g., "EST", "PST")
   */
  getTimezoneAbbreviation(timezone?: string, date?: Date): string {
    const tz = timezone || this.getUserTimezone();
    const targetDate = date || new Date();
    
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      });
      
      const parts = formatter.formatToParts(targetDate);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      return timeZonePart?.value || tz;
    } catch {
      return tz;
    }
  }

  /**
   * Validate timezone identifier
   */
  isValidTimezone(timezone: string): boolean {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get list of common timezones grouped by region
   */
  getCommonTimezones(): TimezoneOption[] {
    const timezones = [
      // Americas
      { value: 'America/New_York', label: 'Eastern Time (New York)', region: 'Americas' },
      { value: 'America/Chicago', label: 'Central Time (Chicago)', region: 'Americas' },
      { value: 'America/Denver', label: 'Mountain Time (Denver)', region: 'Americas' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)', region: 'Americas' },
      { value: 'America/Toronto', label: 'Eastern Time (Toronto)', region: 'Americas' },
      { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)', region: 'Americas' },
      
      // Europe
      { value: 'Europe/London', label: 'Greenwich Mean Time (London)', region: 'Europe' },
      { value: 'Europe/Paris', label: 'Central European Time (Paris)', region: 'Europe' },
      { value: 'Europe/Berlin', label: 'Central European Time (Berlin)', region: 'Europe' },
      { value: 'Europe/Rome', label: 'Central European Time (Rome)', region: 'Europe' },
      { value: 'Europe/Madrid', label: 'Central European Time (Madrid)', region: 'Europe' },
      
      // Asia Pacific
      { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)', region: 'Asia Pacific' },
      { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)', region: 'Asia Pacific' },
      { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)', region: 'Asia Pacific' },
      { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)', region: 'Asia Pacific' },
      { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)', region: 'Asia Pacific' },
      
      // Other
      { value: 'UTC', label: 'Coordinated Universal Time (UTC)', region: 'Other' }
    ];

    return timezones.map(tz => ({
      ...tz,
      offset: this.getTimezoneOffset(tz.value)
    }));
  }

  /**
   * Get timezone offset string (e.g., "UTC-5", "UTC+9")
   */
  private getTimezoneOffset(timezone: string): string {
    try {
      const now = new Date();
      const utc = dayjs.utc(now);
      const local = utc.tz(timezone);
      const offsetMinutes = local.utcOffset();
      const offsetHours = Math.abs(offsetMinutes) / 60;
      const sign = offsetMinutes >= 0 ? '+' : '-';
      
      if (offsetHours === Math.floor(offsetHours)) {
        return `UTC${sign}${Math.abs(offsetHours)}`;
      } else {
        const hours = Math.floor(Math.abs(offsetHours));
        const minutes = Math.abs(offsetMinutes) % 60;
        return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
      }
    } catch {
      return 'UTC';
    }
  }

  /**
   * Initialize service with stored preferences
   */
  initialize(): void {
    const storedTimezone = localStorage.getItem('userTimezone');
    if (storedTimezone && this.isValidTimezone(storedTimezone)) {
      this.userTimezone = storedTimezone;
    }
  }
}

export const timezoneService = new TimezoneService();
timezoneService.initialize();
